import React, { useContext, useEffect, useState } from 'react'
import useDebounce from '../../util/useDebounce';
import { UnitContext } from '../../App';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { Loading, Tag, Text, TextInput } from '@sede-enterprise/shell-ds-react-framework';
import DataTable from '../../components/DataTable/DataTable';
import { Search } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { mmOrMiles } from '../../util/helperFunctions';
import useFetchRelatedSensors from '../SensorDetails/hooks/useFetchRelatedSensors';
import { DE_INSTALL_HELP, DE_INSTALLED_PROD_UNIT } from '../../util/helperTexts';
import { ratingSentimentMap } from '../../util/constants';

import * as R from "rambda";
const sensor_filter = `sensor.excludeFromUi = 0`;

const INIT_ORDER = "latestCorrosionRatePredictionFromLiveModel";
const INIT_ORDER_DIRECTION = 1;
const INIT_PAGE = 1;
const pageSize = 10;


const RelatedCMLData = ({ units, cmlID, cmlShortName, cmlCorrosionLoop }) => {
    const context = useContext(UnitContext);

    const [currentPage, setCurrentPage] = useState(+sessionStorage.getItem("detailsPage") || INIT_PAGE);
    const [sensorOrder, setSensorOrder] = useState(sessionStorage.getItem("detailsOrder") || INIT_ORDER);
    const [orderDirection, setOrderDirection] = useState(+sessionStorage.getItem("detailsOrderDirection") || INIT_ORDER_DIRECTION);

    const [searchVal, setSearchVal] = useState("");
    const debouncedSearchVal = useDebounce(searchVal, 500);

    const [sensorListFilter, setSensorListFilter] = useState(sensor_filter);

    useEffect(() => {
        if (debouncedSearchVal) {
            const filterValue = `(UPPER(sensor.name) LIKE UPPER('%${debouncedSearchVal}%') OR UPPER(sensor.conditionMonitoringLocation) LIKE UPPER('%${debouncedSearchVal}%')) AND ${sensor_filter}`;
            setSensorListFilter(filterValue);
            setCurrentPage(INIT_PAGE); // Reset the current page
            sessionStorage.removeItem('detailsPage'); // Remove the current page from local storage
        }
        else {
            setSensorListFilter(sensor_filter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchVal]);

    const handleOrderChange = (sortBy, direction) => {
        setSensorOrder(sortBy);
        sessionStorage.setItem('detailsOrder', sortBy);

        setOrderDirection(+direction);
        sessionStorage.setItem('detailsOrderDirection', +direction);
    }

    const handleCurrentPageChange = (current) => {
        sessionStorage.setItem('detailsPage', current)
        setCurrentPage(current);
    }

    const orderString = orderDirection === 1 ? `DESC` : `ASC`;
    //const { data, error, loading } = useFetchRelatedSensors(currentPage, pageSize, orderString, sensorListFilter, cmlCorrosionLoop);
    const { data, error, loading } = useFetchRelatedSensors(currentPage, pageSize, sensorOrder, orderString, sensorListFilter, cmlCorrosionLoop);

    /** @type import('@tanstack/react-table').ColumnDef<any> */
    
    const columns = [
            {
                header: 'Site',
                accessorFn: row => row.site,
                id: 'site',
            },
            {
                header: 'Prod Unit',
                accessorFn: row => row.productionUnit,
                id: 'productionUnit',
            },
            {
                header: 'Unit',
                accessorFn: row => row.unit,
                id: 'unit',
            },
            {
                header: 'Equipment',
                accessorFn: row => row.equipment,
                id: 'equipment',
            },
            {
                header: 'Condition Monitoring Location',
                accessorFn: row => row.conditionMonitoringLocation,
                cell: (props) => {
                    const row = props.row.original;
                    return row.conditionMonitoringLocation ? (
                        <Link
                            className="link"
                            to={`/cmls/${encodeURIComponent(row.cmLocation)}/details`}
                            id='sensor-list-id-1'
                        >
                            {row.conditionMonitoringLocation}
                        </Link>
                    ) : null
                },
                id: 'conditionMonitoringLocation',
            },
            {
                header: 'Corrosion Loop',
                accessorFn: row => row.corrosionLoop,
                cell: (props) => {
                    const row = props.row.original;
                    return row.corrosionLoop ? (
                        <Link
                            className="link"
                            to={`/cls/${encodeURIComponent(row.corrosionLoop)}/details`}
                            id='sensor-list-id-2'
                        >
                            {row.corrosionLoop}
                        </Link>
                    ) : null
                },
                id: 'corrosionLoop',
            },
            {
                header: 'Sensor',
                accessorFn: row => row.name,
                cell: (props) => {
                    const row = props.row.original;
                    return row.name ? (
                        <Link
                            className={
                                row.uninstalled ? "link redContent" : "link blueContent"
                            }
                            to={`/home/${encodeURIComponent(row.id)}/details`}
                            id='sensor-list-id-3'
                        >
                            {row.isRealSensor && row.name}
                            {!row.isRealSensor && (
                                <span
                                    title="This CML has no Sensor installed"
                                    className="redContent"
                                    id='sensor-list-id-4'
                                > No Sensor
                                </span>
                            )}
                        </Link>
                    ) : null
                },
                id: 'name',
            },
            {
                header: "Latest PCR (" + mmOrMiles(context.units) + ")",
                cell: (props) => {
                    const row = props.row.original;
                    return context.units
                        ? R.path(["latestCorrosionRatePredictionFromLiveModelMilsPYear"], row)?.toFixed(
                            3) : R.path(["latestCorrosionRatePredictionFromLiveModel"], row)?.toFixed(
                                3)
                },
                accessorFn: (row) =>
                    context.units
                        ? R.path(["latestCorrosionRatePredictionFromLiveModelMilsPYear"], row)?.toFixed(
                            3) : R.path(["latestCorrosionRatePredictionFromLiveModel"], row)?.toFixed(
                                3),
                id: context.units ? "latestCorrosionRatePredictionFromLiveModelMilsPYear" : "latestCorrosionRatePredictionFromLiveModel",
            },
            {
                header: "Average PCR (" + mmOrMiles(context.units) + ")",
                cell: (props) => {
                    const row = props.row.original;
                    return context.units
                        ? R.path(["averageCorrosionRatePredictionFromLiveModelMilsPYear"], row)?.toFixed(
                            3) : R.path(["averageCorrosionRatePredictionFromLiveModel"], row)?.toFixed(
                                3)
                },
                accessorFn: (row) =>
                    context.units
                        ? R.path(["averageCorrosionRatePredictionFromLiveModelMilsPYear"], row)?.toFixed(
                            3) : R.path(["averageCorrosionRatePredictionFromLiveModel"], row)?.toFixed(
                                3),
                id: context.units ? "averageCorrosionRatePredictionFromLiveModelMilsPYear" : "averageCorrosionRatePredictionFromLiveModel",
            },
            {
                header: 'Rating',
                cell: (props) => {
                    const row = props.row.original;
                    return (
                        <Tag
                            sentiment={ratingSentimentMap[row.sensorRating]}
                            variant='outlined'
                            size='small'
                            shape='round'
                            id='sensor-list-id-5'
                        >
                            {row.sensorRating}
                        </Tag>
                    )
                },
                id: "sensorRating",
                accessorFn: (row) => row.sensorRating
            },
            {
                header: 'Fluid',
                accessorFn: row => row.fluid,
                id: 'fluid',
            },
            {
                header: 'Material',
                accessorFn: row => row.material,
                id: 'material',
            },
            {
                header: 'Position',
                accessorFn: row => row.position,
                id: 'position',
            },
            ];
    return (
        <div className='related-container'>
            <div className='related-header'>

                <div className='search-wrapper'>
                    <TextInput
                        prefix={{ node: <Search className='search-icon' /> }}
                        placeholder='Search by Sensor or CML name'
                        onChange={(e) => {
                            setSearchVal(e.target.value);
                        }}
                        value={searchVal}
                    />
                </div>
            </div>
            <br />
            
            <div className='content-body' id='sensor-list-id-11'>
                    {(!!error || (!loading && data?.sensorList?.length === 0)) && <div className='empty-container' id='sensor-list-id-16'>
                        <Text id='sensor-list-id-13'>No Sensors to display</Text>
                    </div>}
                    {data?.sensorList?.length <= 0 && loading && <div className='loading-container' id='sensor-list-id-18' role='status'><Loading id='sensor-list-id-19' /></div>}
                    {data?.sensorList?.length > 0 &&
                        <DataTable data={data?.sensorList} columns={columns} count={data?.sensorCount?.TotalRows}
                            currentPage={+sessionStorage.getItem("detailsPage") || INIT_PAGE}
                            handleCurrentPageChange={handleCurrentPageChange}
                            handleOrderChange={handleOrderChange}
                            INIT_ORDER={sessionStorage.getItem("detailsOrder") || INIT_ORDER}
                            INIT_ORDER_DIRECTION={+sessionStorage.getItem("detailsOrderDirection") || INIT_ORDER_DIRECTION}
                            loading={loading}
                            // paginationMode='client'
                            highlightSensor={cmlShortName}
                        />}

            </div>
        </div>
    )
}

export default RelatedCMLData